import * as React from 'react';
import { observable, computed, action } from 'mobx';
import { observer } from 'mobx-react';

// Types
import { ISelectablePeopleItem } from './AppNetworkPeopleSearch';

// Services
import {
  createQuestion,
  createQuestionOptions,
  createQuestionRecipients
} from '../../services/question';

// Components
import Button from '../../components/Button';
import Dialog from '../../components/Dialog';
import Divider from '../../components/Divider';
import Icon from '../../components/Icon';
import MobileStepper from '../../components/MobileStepper';

import QuestionDialogPeople from './QuestionDialogPeople';
import QuestionDialogCreateForm, {
  QuestionDialogCreateFormState,
  convertDurationValue
} from './QuestionDialogCreateForm';
import QuestionDialogConfirm from './QuestionDialogConfirm';

// Style
import './UserDialog.css';

// Component
interface IProps {
  networkId: string;
  state: { open: boolean };
  selectedPeople: ISelectablePeopleItem[];
  onClose: () => void;
  onExited?: () => void;
  onPeopleRemove: (item: ISelectablePeopleItem) => void;
  onCreated: () => void;
}

class UserDialog extends React.Component<IProps, {}> {
  @observable stepperIndex = 0;

  formState = new QuestionDialogCreateFormState();

  @computed
  get isNextDisabled() {
    if (this.stepperIndex === 0 && this.props.selectedPeople.length === 0) {
      return true;
    }
    return false;
  }

  @action
  handleDialogExited = () => {
    this.stepperIndex = 0;
    this.formState.description.reinitValue('');
  };

  handleCreateFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const validation = await this.formState.state.validate();
    if (validation.hasError) {
      return;
    }
    this.stepperIndex += 1;
  };

  handleMobileStepperBack = () => {
    if (this.stepperIndex <= 0) {
      this.props.onClose();
      return;
    }

    this.stepperIndex -= 1;
  };

  handleMobileStepperNext = async () => {
    if (this.stepperIndex >= 2) {
      const { networkId } = this.props;
      const { description, duration } = this.formState;
      const dateTimes = convertDurationValue(duration.$);

      const { questionId } = await createQuestion({
        params: {
          networkId
        },
        body: {
          description: description.$,
          activeFrom: dateTimes[0].toISOString(),
          activeTo: dateTimes[1].toISOString()
        }
      });

      await createQuestionOptions({
        params: {
          networkId
        },
        body: [
          {
            questionId,
            networkId,
            value: 'Yes',
            sorting: 1
          },
          {
            questionId,
            networkId,
            value: 'No',
            sorting: 2
          }
        ]
      });

      await createQuestionRecipients({
        params: {
          networkId
        },
        body: this.props.selectedPeople.map(item => ({
          networkId,
          questionId,
          userId: item.userId
        }))
      });

      this.props.onCreated();
      return;
    }

    if (this.stepperIndex === 1) {
      const validation = await this.formState.state.validate();
      if (validation.hasError) {
        return;
      }
    }

    this.stepperIndex += 1;
  };

  render() {
    return (
      <Dialog
        open={this.props.state.open}
        onClose={this.props.onClose}
        onExited={this.handleDialogExited}
        classes={{ paper: 'AppNetworkPeople_UserDialog_Paper' }}
      >
        <div style={{ height: 400, overflow: 'auto' }}>
          {this.stepperIndex === 0 ? (
            <QuestionDialogPeople
              selectPeople={this.props.selectedPeople}
              onRemove={this.props.onPeopleRemove}
            />
          ) : null}
          {this.stepperIndex === 1 ? (
            <QuestionDialogCreateForm
              formState={this.formState}
              onSubmit={this.handleCreateFormSubmit}
            />
          ) : null}
          {this.stepperIndex === 2 ? <QuestionDialogConfirm /> : null}
        </div>
        <Divider />
        <MobileStepper
          activeStep={this.stepperIndex}
          type="dots"
          position="static"
          backButton={
            <Button
              dense={true}
              onClick={this.handleMobileStepperBack}
              disabled={this.stepperIndex === 0}
            >
              <Icon>keyboard_arrow_left</Icon>
              Back
            </Button>
          }
          nextButton={
            <Button
              dense={true}
              onClick={this.handleMobileStepperNext}
              disabled={this.isNextDisabled}
            >
              {this.stepperIndex >= 2 ? 'Create' : 'Next'}
              <Icon>keyboard_arrow_right</Icon>
            </Button>
          }
          steps={3}
        />
      </Dialog>
    );
  }
}

export default observer(UserDialog);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/views/AppNetworkPeopleSearch/QuestionDialog.tsx